import React from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import useAgents from '../hooks/useAgents';
import { AgentDataTable } from '../components/agents/table/data-table';
import { agentColumns } from '../components/agents/table/columns';
import { Input } from '../components/ui/input';
import { Search, Users, Loader2 } from 'lucide-react';

/**
 * Agents page component
 * @returns {JSX.Element} AgentsPage component
 */
const AgentsPage = () => {
  const navigate = useNavigate();
  const {
    agents,
    loading,
    error,
    searchParams,
    updateSearchParams,
    clearSearchParams,
    refreshAgents
  } = useAgents();

  const handleAgentClick = (agent) => {
    navigate(`/agents/${agent.id}`);
  };

  const handleKeywordChange = (e) => {
    updateSearchParams({ keyword: e.target.value });
  };

  const handleAgentIdChange = (e) => {
    const value = e.target.value;
    // Only allow numeric input
    if (value === '' || /^\d*$/.test(value)) {
      updateSearchParams({ agentId: value });
    }
  };

  const handleClearSearch = () => {
    clearSearchParams();
  };

  return (
    <MainLayout activeTab="agents">
      <div className="flex flex-col h-screen">
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-4 bg-white border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="w-6 h-6 text-blue-600" />
              <h1 className="text-xl font-semibold text-gray-900">Agents</h1>
            </div>
            <div className="text-sm text-gray-500">
              {agents.length} agent{agents.length !== 1 ? 's' : ''} found
            </div>
          </div>
        </div>

        {/* Search controls */}
        <div className="flex-shrink-0 px-6 py-4 bg-gray-50 border-b">
          <div className="flex flex-col md:flex-row gap-4 max-w-4xl">
            {/* Agent ID Search */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Agent ID
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search by Agent ID..."
                  className="pl-9"
                  value={searchParams.agentId}
                  onChange={handleAgentIdChange}
                />
              </div>
            </div>

            {/* Keyword Search */}
            <div className="flex-[2]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search by name, email, or phone..."
                  className="pl-9"
                  value={searchParams.keyword}
                  onChange={handleKeywordChange}
                />
              </div>
            </div>

            {/* Clear Button */}
            <div className="flex items-end">
              <button
                onClick={handleClearSearch}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {/* Agents table */}
        <div className="flex-1 bg-white min-h-0 overflow-hidden">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full">
              <Loader2 className="w-10 h-10 text-blue-600 animate-spin mb-4" />
              <p className="text-gray-600">Loading agents...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full">
              <div className="bg-red-100 text-red-700 p-4 rounded-lg mb-4 max-w-md">
                <p>{error}</p>
              </div>
              <button
                onClick={refreshAgents}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : agents.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Users className="w-16 h-16 text-gray-300 mb-4" />
              <p className="text-gray-600 text-lg">No agents found</p>
              <p className="text-gray-500 text-sm mt-1">
                {searchParams.keyword || searchParams.agentId
                  ? 'Try adjusting your search criteria'
                  : 'No agents available in the system'}
              </p>
            </div>
          ) : (
            <AgentDataTable
              columns={agentColumns}
              data={agents}
              onAgentClick={handleAgentClick}
            />
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default AgentsPage;
